import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { QrCode, CheckCircle, Clock, X, Loader2, Copy, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const QRCollectModal = ({ isOpen, onClose, groupId, payerUserId, payerName, amountCents, onPaymentReceived }) => {
  const [requestId, setRequestId] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      createRequest();
    }
    return () => cleanup();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      cleanup();
      setRequestId(null);
      setPaymentUrl('');
      setExpiresAt(null);
      setTimeLeft(0);
      setStatus('loading');
      setErrorMsg('');
    }
  }, [isOpen]);

  const cleanup = () => {
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
  };

  const createRequest = async () => {
    setStatus('loading');
    cleanup();
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          group_id: groupId,
          to_user_id: payerUserId,
          amount_cents: amountCents,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setRequestId(data.request_id);
        setPaymentUrl(data.url);
        setExpiresAt(new Date(data.expires_at));
        setStatus('ready');
        startCountdown(new Date(data.expires_at));
        startPolling(data.request_id);
      } else {
        const err = await response.json();
        setErrorMsg(err.detail || 'Failed to create payment request');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Connection failed');
      setStatus('error');
    }
  };

  const startCountdown = (expiry) => {
    clearInterval(timerRef.current);
    const tick = () => {
      const diff = Math.max(0, Math.floor((expiry - new Date()) / 1000));
      setTimeLeft(diff);
      if (diff <= 0) {
        setStatus('expired');
        cleanup();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  };

  const startPolling = (rid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/requests/${rid}/status`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'paid') {
            setStatus('paid');
            cleanup();
            toast.success('Payment received!');
            onPaymentReceived();
          }
        }
      } catch {}
    }, 2500);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentUrl);
      toast.success('Link copied!');
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = paymentUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('Link copied!');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Payo Payment',
          text: `Pay $${(amountCents / 100).toFixed(2)}`,
          url: paymentUrl,
        });
      } catch {}
    } else {
      handleCopyLink();
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="qr-collect-modal">
      <Card className="max-w-sm w-full">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold flex items-center">
            <QrCode className="w-5 h-5 mr-2 text-blue-600" />
            Collect Payment
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="qr-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">

          {status === 'loading' && (
            <div className="py-12" data-testid="qr-loading">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          )}

          {status === 'error' && (
            <div className="py-8 text-center" data-testid="qr-error">
              <p className="text-red-600 text-sm mb-3">{errorMsg}</p>
              <Button onClick={createRequest} size="sm">Retry</Button>
            </div>
          )}

          {status === 'ready' && (
            <>
              <p className="text-sm text-gray-600">
                {payerName} scans to pay you
              </p>
              <p className="text-3xl font-bold text-gray-900">
                ${(amountCents / 100).toFixed(2)}
              </p>

              <div className="p-4 bg-white rounded-xl border-2 border-gray-100" data-testid="qr-code-container">
                <QRCodeSVG value={paymentUrl} size={200} level="M" />
              </div>

              <div className="flex items-center space-x-2 text-sm" data-testid="qr-countdown">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className={`font-mono font-medium ${timeLeft < 60 ? 'text-red-500' : 'text-gray-600'}`}>
                  {formatTime(timeLeft)}
                </span>
                <span className="text-gray-400">remaining</span>
              </div>

              <div className="flex space-x-3 w-full">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleCopyLink}
                  data-testid="qr-copy-link-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy link
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleShare}
                  data-testid="qr-share-link-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share link
                </Button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Single-use link · expires automatically
              </p>
            </>
          )}

          {status === 'paid' && (
            <div className="py-8 text-center" data-testid="qr-paid">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-xl font-bold text-green-700">Payment Received!</p>
              <p className="text-sm text-gray-500 mt-2">
                {payerName} paid ${(amountCents / 100).toFixed(2)}
              </p>
              <Button onClick={onClose} className="mt-4 bg-green-600 hover:bg-green-700 text-white" data-testid="qr-done-btn">
                Done
              </Button>
            </div>
          )}

          {status === 'expired' && (
            <div className="py-8 text-center" data-testid="qr-expired">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-lg font-semibold text-gray-700">Expired</p>
              <Button onClick={createRequest} className="mt-4" data-testid="qr-regenerate-btn">
                Generate New QR
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QRCollectModal;
